import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { documentKey, emptyBoard, legacyBoard, type BoardDocument, type BoardResponse } from './boardModel';
import type { BoardKind } from './boardAvatarApi';

export interface BoardSaveStatus { kind: 'loading' | 'saved' | 'saving' | 'dirty' | 'error' | 'conflict'; message?: string }
interface Session { document: BoardDocument; saved: string; revision: number; pending: boolean; blocked: boolean; live: boolean; uncertain?: { keys: string[]; revision: number } }

export function useBoardSession(assetId: number, kind: BoardKind = 'asset') {
  const [document, setDocument] = useState<BoardDocument>(emptyBoard);
  const [status, setStatus] = useState<BoardSaveStatus>({ kind: 'loading' });
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const state = useRef<Session>({ document: emptyBoard(), saved: '', revision: 0, pending: false, blocked: false, live: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pump = useRef<() => void>(() => {});

  useEffect(() => {
    let alive = true;
    const session: Session = { document: emptyBoard(), saved: '', revision: 0, pending: false, blocked: false, live: true };
    state.current = session;
    setLoaded(false); setCanEdit(false); setStatus({ kind: 'loading' }); setDirty(false);
    void (kind === 'project' ? api.projectBoard(assetId) : api.assetBoard(assetId)).then(result => {
      if (!alive) return;
      const initial = result.document ?? legacyBoard(result.legacyHtml);
      session.document = initial; session.saved = documentKey(initial); session.revision = result.revision;
      setDocument(initial); setCanEdit(result.canEdit); setLoaded(true); setStatus({ kind: 'saved' });
    }).catch((error: unknown) => {
      if (alive) setStatus({ kind: 'error', message: error instanceof Error ? error.message : 'Не удалось открыть доску.' });
    });
    return () => { alive = false; session.live = false; clearTimeout(timer.current); };
  }, [assetId, kind, loadKey]);

  const save = useCallback(() => {
    const session = state.current;
    if (!session.live || session.pending || session.blocked) return;
    const snapshot = session.document, serialized = documentKey(snapshot), sentRevision = session.revision;
    if (serialized === session.saved && !session.uncertain) { setDirty(false); setStatus({ kind: 'saved' }); return; }
    session.pending = true; setStatus({ kind: 'saving' });
    const accepted = (result: BoardResponse) => {
      if (!session.live) return;
      session.revision = result.revision; session.saved = serialized; session.uncertain = undefined;
      const changed = documentKey(session.document) !== serialized;
      setDirty(changed); setStatus({ kind: changed ? 'dirty' : 'saved' });
    };
    void (kind === 'project' ? api.saveProjectBoard(assetId, snapshot, session.revision) : api.saveAssetBoard(assetId, snapshot, session.revision)).then(accepted).catch((error: unknown) => {
      if (!session.live) return;
      const current = error instanceof ApiError && error.status === 409 ? (error.details as { current?: BoardResponse } | undefined)?.current : undefined;
      // A response may have been lost after a successful write. A retry must not
      // turn that acknowledged document into a conflict or overwrite newer edits.
      if (current?.document && documentKey(current.document) === serialized) { accepted(current); return; }
      if (current?.document && session.uncertain && current.revision === session.uncertain.revision + 1 && session.uncertain.keys.includes(documentKey(current.document))) {
        session.revision = current.revision; session.saved = documentKey(current.document); session.uncertain = undefined;
        setDirty(true); setStatus({ kind: 'dirty' }); return;
      }
      if (!(error instanceof ApiError) || error.status === 0 || error.status >= 500) session.uncertain = { keys: [...new Set([...(session.uncertain?.revision === sentRevision ? session.uncertain.keys : []), serialized])], revision: sentRevision };
      session.blocked = true;
      setDirty(true);
      setStatus({ kind: current || error instanceof ApiError && error.status === 409 ? 'conflict' : 'error', message: current ? 'Коллега уже изменил эту доску. Сохраните свою копию и откройте версию с сервера.' : error instanceof Error ? error.message : 'Не удалось сохранить доску.' });
    }).finally(() => {
      session.pending = false;
      if (session.live && !session.blocked && documentKey(session.document) !== session.saved) timer.current = setTimeout(() => pump.current(), 500);
    });
  }, [assetId, kind]);
  useEffect(() => { pump.current = save; }, [save]);

  const change = useCallback((next: BoardDocument) => {
    const session = state.current;
    if (!session.live || !canEdit || !loaded) return;
    session.document = next; setDocument(next);
    const changed = documentKey(next) !== session.saved || session.pending || Boolean(session.uncertain);
    setDirty(changed);
    if (!session.blocked && !session.pending) setStatus({ kind: changed ? 'dirty' : 'saved' });
    clearTimeout(timer.current);
    if (changed && !session.blocked) timer.current = setTimeout(() => pump.current(), 800);
  }, [canEdit, loaded]);
  const retry = () => {
    if (!loaded) { setLoadKey(key => key + 1); return; }
    state.current.blocked = false; save();
  };
  const reload = () => setLoadKey(key => key + 1);
  return { document, change, status, dirty, canEdit, loaded, retry, reload };
}
